import PropTypes from 'prop-types'
import React from 'react'

import CtaLink from '../CtaLink'

import './styles.css'

// SPEC-053 FR-1..FR-3: the featured course's complete curriculum in course
// order — free chapters playable in place, the rest locked. The gaps are
// the sales argument: the viewer sees exactly which chapters they're
// missing, positioned where they belong.
//
// Locked chapters are real anchors to the trial (Pedro's Q4 decision): a
// click on a locked chapter is the clearest buying signal on the page and
// must not dead-end — and must not leave the funnel either. The tile
// advertises its clickability: cursor, hover/focus state, and the padlock
// swaps to an "Unlock with free trial" chip.

// same conventions as the course page's curriculum (NewCurriculum): chapter
// titles in the DB carry a " - HH:MM:SS" timecode suffix that never renders
export const removeAfterDash = (str) => str.replace(/\s* - .*/, '').trim()
export const formatDuration = (seconds) =>
  seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)}min`

function Curriculum({
  chapters,
  chaptersCount,
  trialCtaUrl = '/subscriptions/plans',
  onPlay,
  onLockedClick
}) {
  if (!chapters.length) return null
  return (
    <section className="lm-curriculum">
      <div className="lm-curriculum__inner wrapper">
        <h2 className="lm-curriculum__heading">
          Course curriculum
          {chaptersCount > 0 && (
            <span className="lm-curriculum__count">
              {' '}
              &middot; {chaptersCount} lessons
            </span>
          )}
        </h2>
        <div className="lm-curriculum__grid">
          {chapters.map((chapter) => {
            const eyebrow = [
              `Lesson ${chapter.number}`,
              chapter.durationSeconds > 0 &&
                formatDuration(chapter.durationSeconds)
            ]
              .filter(Boolean)
              .join(' · ')
            const thumb = (
              <span
                className="lm-chapter__thumb"
                style={
                  chapter.thumbnail?.default
                    ? { backgroundImage: `url(${chapter.thumbnail.default})` }
                    : undefined
                }
              >
                {chapter.free ? (
                  <i className="icon-play" aria-hidden="true" />
                ) : (
                  <>
                    <span className="lm-chapter__padlock" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="18" height="18">
                        <path
                          fill="currentColor"
                          d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 1 1 6 0v3H9Z"
                        />
                      </svg>
                    </span>
                    <span className="lm-chapter__chip">
                      <span>Unlock with free trial</span>
                    </span>
                  </>
                )}
              </span>
            )
            const meta = (
              <>
                <span className="lm-chapter__eyebrow">{eyebrow}</span>
                <span className="lm-chapter__title">
                  {removeAfterDash(chapter.title)}
                </span>
              </>
            )
            return chapter.free ? (
              <button
                key={chapter.id}
                type="button"
                className="lm-chapter -free"
                onClick={() => onPlay(chapter.video)}
                aria-label={`Play lesson ${chapter.number}: ${removeAfterDash(chapter.title)}`}
              >
                {thumb}
                {meta}
              </button>
            ) : (
              <CtaLink
                key={chapter.id}
                to={trialCtaUrl}
                className="lm-chapter -locked"
                onClick={() => onLockedClick(chapter)}
              >
                {thumb}
                {meta}
              </CtaLink>
            )
          })}
        </div>
      </div>
    </section>
  )
}

Curriculum.propTypes = {
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      durationSeconds: PropTypes.number,
      thumbnail: PropTypes.shape({ default: PropTypes.string }),
      free: PropTypes.bool,
      video: PropTypes.shape({
        slug: PropTypes.string,
        videoHi: PropTypes.string,
        videoLow: PropTypes.string,
        vttFile: PropTypes.string,
        subtitles: PropTypes.array
      })
    })
  ).isRequired,
  chaptersCount: PropTypes.number,
  trialCtaUrl: PropTypes.string,
  onPlay: PropTypes.func.isRequired,
  onLockedClick: PropTypes.func.isRequired
}

Curriculum.defaultProps = {
  chaptersCount: 0,
  trialCtaUrl: '/subscriptions/plans'
}

export default Curriculum
